import { buildHostMessage, type HostMessage } from '@cesto/web-protocol';
import {
  connectWallet,
  detectWallets,
  mapWalletError,
  signWalletMessage,
  signWalletTransaction,
  type WalletErrorCode,
} from './wallet-relay';

/** The popup to host request types the relay answers. */
export type WalletRequestType =
  | 'wallet:detect'
  | 'wallet:connect'
  | 'wallet:sign-message'
  | 'wallet:sign-transaction';

const FALLBACK_CODES: Record<WalletRequestType, WalletErrorCode> = {
  'wallet:detect': 'wallet_unavailable',
  'wallet:connect': 'connect_failed',
  'wallet:sign-message': 'sign_failed',
  'wallet:sign-transaction': 'sign_failed',
};

/**
 * Answers one wallet relay request from the invest dialog. Every request
 * gets exactly one response: the matching success message or
 * `wallet:error`, always echoing the request's correlation `id` verbatim.
 * The caller supplies `reply`, which posts to the pinned frame window with
 * the Cesto origin as targetOrigin.
 */
export async function handleWalletRequest(
  type: WalletRequestType,
  payload: Record<string, unknown>,
  reply: (message: HostMessage) => void,
): Promise<void> {
  const id = typeof payload.id === 'string' ? payload.id : '';
  try {
    if (type === 'wallet:detect') {
      reply(
        buildHostMessage('wallet:detected', { id, wallets: detectWallets() }),
      );
      return;
    }
    const walletId =
      typeof payload.walletId === 'string' ? payload.walletId : '';
    if (type === 'wallet:connect') {
      const address = await connectWallet(walletId);
      reply(buildHostMessage('wallet:address', { id, address }));
      return;
    }
    if (type === 'wallet:sign-transaction') {
      const transaction = await signWalletTransaction(
        walletId,
        typeof payload.transaction === 'string' ? payload.transaction : '',
        typeof payload.address === 'string' ? payload.address : undefined,
      );
      reply(buildHostMessage('wallet:transaction', { id, transaction }));
      return;
    }
    const message = typeof payload.message === 'string' ? payload.message : '';
    const signature = await signWalletMessage(walletId, message);
    reply(buildHostMessage('wallet:signature', { id, signature }));
  } catch (error) {
    const { code, message } = mapWalletError(error, FALLBACK_CODES[type]);
    reply(buildHostMessage('wallet:error', { id, code, message }));
  }
}
